import React from 'react';
import {
  BlockOutlined,
  ScanOutlined,
  CopyOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  FileTextOutlined,
  FilePptOutlined,
  CheckCircleFilled,
  PictureOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../components/language/LanguageContext';
import styles from './Features.module.scss';

interface FeatureSectionProps {
  title: string;
  description: string;
  isReversed?: boolean;
  visual: React.ReactNode;
  icon: React.ReactNode;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
  title,
  description,
  isReversed,
  visual,
  icon
}) => (
  <div className={`${styles.featureSection} ${isReversed ? styles.reversed : ''}`}>
    <div className={styles.textContent}>
      <div className={styles.iconBadge}>
        {icon}
      </div>
      <h3 className={styles.title}>{title}</h3>
      <p className={styles.description}>{description}</p>
    </div>

    <div className={styles.visualContent}>
      <div className={styles.visualWrapper}>
        <div className={styles.gridBackground}></div>
        <div className={styles.visualInner}>
          {visual}
        </div>
      </div>
    </div>
  </div>
);

/* --- Custom Visual Components --- */

const VisualFormatRetention = ({ t }: { t: any }) => (
  <div className={styles.formatRetentionVisual}>
    {/* Source Doc (English) */}
    <div className={styles.sourceDoc}>
      <div className={styles.docHeader}>
        <span className={styles.headerLabel}>{t('landing.features.visuals.original')}</span>
      </div>

      <div className={styles.docImage}>
        <PictureOutlined className={styles.imageIcon} />
      </div>

      <div className={styles.docContent}>
        <div className={`${styles.textLine} ${styles.titleLine}`}></div>
        <div className={styles.textLine}></div>
        <div className={styles.textLine}></div>
        <div className={`${styles.textLine} ${styles.shortLine}`}></div>
      </div>

      <div className={styles.docColumns}>
        <div className={styles.column}>
          <div className={styles.columnBox}></div>
          <div className={styles.columnLine}></div>
        </div>
        <div className={styles.column}>
          <div className={styles.columnBox}></div>
          <div className={styles.columnLine}></div>
        </div>
      </div>
    </div>

    {/* Transition Icon */}
    <div className={styles.transitionIcon}>
      <ArrowRightOutlined />
    </div>

    {/* Target Doc (Chinese/Other) */}
    <div className={styles.targetDoc}>
      <div className={styles.highlightBar}></div>

      <div className={styles.docHeader}>
        <span className={`${styles.headerLabel} ${styles.translated}`}>
          {t('landing.features.visuals.translated')}
        </span>
      </div>

      <div className={styles.docImage}>
        <PictureOutlined className={styles.imageIcon} />
      </div>

      <div className={styles.docContent}>
        <div className={`${styles.textLine} ${styles.titleLine} ${styles.translatedLine}`}></div>
        <div className={`${styles.textLine} ${styles.translatedLine}`}></div>
        <div className={`${styles.textLine} ${styles.translatedLine}`}></div>
        <div className={`${styles.textLine} ${styles.shortLine} ${styles.translatedLine}`}></div>
      </div>

      <div className={styles.docColumns}>
        <div className={styles.column}>
          <div className={styles.columnBox}></div>
          <div className={`${styles.columnLine} ${styles.translatedLine}`}></div>
        </div>
        <div className={styles.column}>
          <div className={styles.columnBox}></div>
          <div className={`${styles.columnLine} ${styles.translatedLine}`}></div>
        </div>
      </div>
    </div>
  </div>
);

const VisualUnstructured = ({ t }: { t: any }) => (
  <div className={styles.unstructuredVisual}>
    <div className={styles.scanLine}></div>

    <div className={styles.unstructuredContent}>
      <div className={`${styles.detectedSection} ${styles.imageSection}`}>
        <div className={styles.detectedLabel}>{t('landing.features.visuals.imgDetected')}</div>
        <div className={styles.imageContainer}>
          <div className={styles.imageBox}>
            <PictureOutlined />
          </div>
          <div className={styles.textPlaceholder}>
            <div className={styles.placeholderLine}></div>
            <div className={`${styles.placeholderLine} ${styles.short}`}></div>
          </div>
        </div>
      </div>

      <div className={`${styles.detectedSection} ${styles.tableSection}`}>
        <div className={styles.detectedLabel}>{t('landing.features.visuals.tableDetected')}</div>
        <div className={styles.tableContainer}>
          <div className={styles.tableRow}>
            <div className={styles.tableCell}></div>
            <div className={styles.tableCell}></div>
            <div className={styles.tableCell}></div>
          </div>
          <div className={styles.tableRow}>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
          </div>
          <div className={styles.tableRow}>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
          </div>
          <div className={styles.tableRow}>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
            <div className={`${styles.tableCell} ${styles.dataCell}`}></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const VisualFullDoc = ({ t }: { t: any }) => (
  <div className={styles.fullDocVisual}>
    <div className={`${styles.stackDoc} ${styles.stack1}`}></div>
    <div className={`${styles.stackDoc} ${styles.stack2}`}></div>

    <div className={styles.mainDoc}>
      <div className={styles.docIcon}>
        <FileTextOutlined />
      </div>
      <div className={styles.docLines}>
        <div className={styles.docLine}></div>
        <div className={styles.docLine}></div>
        <div className={`${styles.docLine} ${styles.shortDoc}`}></div>
      </div>

      <div className={styles.processedBadge}>
        <CheckCircleFilled className={styles.checkIcon} />
        <div className={styles.badgeText}>
          <span className={styles.badgeTitle}>{t('landing.features.visuals.processed')}</span>
          <span className={styles.badgeSubtitle}>{t('landing.features.visuals.pages')}</span>
        </div>
      </div>
    </div>
  </div>
);

const VisualExport = ({ t }: { t: any }) => (
  <div className={styles.exportVisual}>
    {/* PDF Card */}
    <div className={`${styles.exportCard} ${styles.pdfCard}`}>
      <div className={styles.cardDot}></div>
      <div className={styles.cardIcon}>
        <FileTextOutlined />
      </div>
      <div className={styles.cardInfo}>
        <div className={styles.cardTitle}>PDF</div>
        <div className={styles.cardSubtitle}>{t('landing.features.visuals.printReady')}</div>
      </div>
      <div className={styles.cardHighlight}></div>
    </div>

    {/* PPTX Card */}
    <div className={`${styles.exportCard} ${styles.pptxCard}`}>
      <div className={styles.cardDot}></div>
      <div className={styles.cardIcon}>
        <FilePptOutlined />
      </div>
      <div className={styles.cardInfo}>
        <div className={styles.cardTitle}>PPTX</div>
        <div className={styles.cardSubtitle}>{t('landing.features.visuals.fullyEditable')}</div>
      </div>
      <div className={styles.cardHighlight}></div>
    </div>
  </div>
);

const Features: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section id="features" className={styles.features}>
      <div className={styles.featuresContainer}>
        <FeatureSection
          icon={<BlockOutlined />}
          title={t('landing.features.f1.title')}
          description={t('landing.features.f1.desc')}
          visual={<VisualFormatRetention t={t} />}
          isReversed={false}
        />

        <FeatureSection
          icon={<ScanOutlined />}
          title={t('landing.features.f2.title')}
          description={t('landing.features.f2.desc')}
          visual={<VisualUnstructured t={t} />}
          isReversed={true}
        />

        <FeatureSection
          icon={<CopyOutlined />}
          title={t('landing.features.f3.title')}
          description={t('landing.features.f3.desc')}
          visual={<VisualFullDoc t={t} />}
          isReversed={false}
        />

        <FeatureSection
          icon={<DownloadOutlined />}
          title={t('landing.features.f4.title')}
          description={t('landing.features.f4.desc')}
          visual={<VisualExport t={t} />}
          isReversed={true}
        />
      </div>
    </section>
  );
};

export default Features;
